/**
 * Read one PowerPoint template in full for the editor.
 *
 * The workspace template list may not carry `content` (it is large, and the list only needs a
 * summary), and a template created moments ago by an import may not be in the list yet. The
 * editor reads the row it opens directly, scoped to the active account.
 */

/** Template statuses while the worker is still importing the uploaded deck. */
export const IMPORTING_STATUSES = Object.freeze(['importing', 'processing']);

/**
 * Whether a status means the import is still running.
 * @param {string|null|undefined} status - Template status.
 * @returns {boolean} True while importing.
 */
export function isImportingStatus(status) {
  return IMPORTING_STATUSES.includes(status);
}

/**
 * Whether a template list entry already holds the content the editor needs.
 * @param {object|null|undefined} template - Template from the workspace list.
 * @returns {boolean} True when `content` is a string.
 */
export function hasLoadedContent(template) {
  return Boolean(template) && typeof template.content === 'string';
}

/**
 * Map a full templates row to the editor's template shape.
 * @param {object} row - Database row.
 * @returns {object} Template with camelCase fields.
 */
export function mapFullTemplateRow(row) {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    content: typeof row.content === 'string' ? row.content : null,
    templateType: row.template_type || 'document',
    status: row.status || 'ready',
    sourceDocument: row.source_document ?? null,
    generationMetadata: row.generation_metadata ?? null,
    updatedAt: row.updated_at,
  };
}

/**
 * Fetch one template row in full.
 * @param {object} client - Supabase client.
 * @param {{ templateId: string, accountId?: string|null }} where - Row id and owning account.
 * @returns {Promise<object|null>} Mapped template, or null when there is no such row here.
 */
export async function fetchFullTemplateRow(client, { templateId, accountId }) {
  let query = client.from('templates').select('*').eq('id', templateId);
  if (accountId) query = query.eq('account_id', accountId);
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return data ? mapFullTemplateRow(data) : null;
}

/**
 * Load one template in full: through AppContext's `actions.loadTemplate` when the app has it
 * (it also merges the row into the workspace list), otherwise straight from the database.
 * @param {object} args - Arguments.
 * @param {object} [args.actions] - AppContext actions.
 * @param {object} args.client - Supabase client (fallback read).
 * @param {string} args.templateId - Template id.
 * @param {string|null} [args.accountId] - Active account id.
 * @param {boolean} [args.force] - Skip any cached copy (re-reading an importing template).
 * @returns {Promise<object|null>} Template, or null when there is no such row here.
 */
export async function loadFullTemplate({ actions, client, templateId, accountId, force = false }) {
  if (typeof actions?.loadTemplate === 'function') {
    const result = await actions.loadTemplate(templateId, { force });
    if (result?.success) return result.data || null;
    if (result?.code === 'NOT_FOUND') return null;
    const error = new Error(result?.error || 'The template could not be loaded.');
    if (result?.code) error.code = result.code;
    throw error;
  }
  return fetchFullTemplateRow(client, { templateId, accountId });
}

/**
 * Best reason to show for a failed import, from the row's generation metadata.
 * @param {object|null|undefined} metadata - `generation_metadata`.
 * @returns {string} Reason text, or ''.
 */
export function importFailureReason(metadata) {
  if (!metadata || typeof metadata !== 'object') return '';
  const reason = metadata.failureReason || metadata.reason || metadata.error;
  return typeof reason === 'string' ? reason.slice(0, 300) : '';
}
